import { useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Field, Select, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { usePatientActions } from '@/features/patients/api';
import {
  ALLERGY_GROUP_LABELS,
  ALLERGY_SEVERITY_LABELS,
  type Allergy,
  type AllergyGroup,
  type AllergySeverity,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import styles from './AllergyList.module.css';

/**
 * One allergy as the staff views list it (patient record, consultation, queue,
 * dispensary). Only `substance` is always there; the rest depends on the view.
 */
export type AllergyListItem = Pick<Allergy, 'substance'> &
  Partial<
    Pick<
      Allergy,
      | 'id'
      | 'reaction'
      | 'severity'
      | 'allergyGroup'
      | 'status'
      | 'fromMergedRecord'
      | 'fromRecord'
    >
  >;

const REVIEW_PERMISSION = 'encounters:write';

const GROUPS = Object.keys(ALLERGY_GROUP_LABELS) as AllergyGroup[];
const SEVERITIES = Object.keys(ALLERGY_SEVERITY_LABELS) as AllergySeverity[];
const isGroup = (v: string | null | undefined): v is AllergyGroup =>
  !!v && v in ALLERGY_GROUP_LABELS;

/**
 * RL-1156: a patient's allergies, the same everywhere staff see them. Each is
 * a danger chip; one the patient reported in the app (RL-951 `status:
 * reported`) carries "Patient-reported, not confirmed", one recorded on
 * another record of the merged-record family (RL-1171) "From merged record
 * {fullName}". With `patientId` and `encounters:write`, a reported allergy can
 * be confirmed (prescribing group, severity) or rejected (reason) here.
 * `onRemove` keeps the consultation's remove button on each chip.
 */
export function AllergyList({
  allergies,
  patientId,
  onRemove,
  label = 'Allergies',
}: {
  allergies: AllergyListItem[];
  /** The record the page shows; enables Confirm / Reject (with the permission). */
  patientId?: string;
  onRemove?: (allergy: AllergyListItem) => void;
  label?: string;
}) {
  const { hasPermission } = useSession();
  const canReview = !!patientId && hasPermission(REVIEW_PERMISSION);
  if (allergies.length === 0) return <>None recorded</>;
  return (
    <ul className={styles.list} aria-label={label}>
      {allergies.map((a, i) => (
        <AllergyItem
          key={a.id ?? `${a.substance}-${i}`}
          allergy={a}
          patientId={canReview ? patientId : undefined}
          onRemove={onRemove}
        />
      ))}
    </ul>
  );
}

/** The chip and its hints, without actions. */
export function AllergyBadge({
  allergy: a,
  onRemove,
}: {
  allergy: AllergyListItem;
  onRemove?: (allergy: AllergyListItem) => void;
}) {
  const reported = a.status === 'reported';
  const meta = [
    a.reaction,
    a.severity ? ALLERGY_SEVERITY_LABELS[a.severity] : null,
  ].filter(Boolean);
  return (
    <span className={styles.badges}>
      <Badge tone="danger">
        {a.substance}
        {onRemove && (
          <button
            type="button"
            className={styles.remove}
            aria-label={`Remove allergy ${a.substance}`}
            onClick={() => onRemove(a)}
          >
            <X size={10} />
          </button>
        )}
      </Badge>
      {reported && (
        <Badge tone="warning">Patient-reported, not confirmed</Badge>
      )}
      {a.fromMergedRecord && (
        <span className={styles.hint}>
          From merged record {a.fromRecord?.fullName ?? '(name not shown)'}
        </span>
      )}
      {reported && meta.length > 0 && (
        <span className={styles.hint}>{meta.join(' · ')}</span>
      )}
    </span>
  );
}

type Mode = 'idle' | 'confirm' | 'reject';

function AllergyItem({
  allergy: a,
  patientId,
  onRemove,
}: {
  allergy: AllergyListItem;
  patientId?: string;
  onRemove?: (allergy: AllergyListItem) => void;
}) {
  const [mode, setMode] = useState<Mode>('idle');
  const allergyId = a.status === 'reported' ? a.id : undefined;
  const reviewable = !!patientId && !!allergyId;
  return (
    <li
      className={
        mode === 'idle' ? styles.item : `${styles.item} ${styles.open}`
      }
    >
      <AllergyBadge allergy={a} onRemove={onRemove} />
      {reviewable && mode === 'idle' && (
        <span className={styles.actions}>
          <Button
            size="sm"
            icon={<Check size={14} />}
            aria-label={`Confirm allergy ${a.substance}`}
            onClick={() => setMode('confirm')}
          >
            Confirm
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Reject allergy ${a.substance}`}
            onClick={() => setMode('reject')}
          >
            Reject
          </Button>
        </span>
      )}
      {patientId && allergyId && mode !== 'idle' && (
        <ReviewForm
          mode={mode}
          patientId={patientId}
          allergyId={allergyId}
          allergy={a}
          onDone={() => setMode('idle')}
        />
      )}
    </li>
  );
}

interface Failure {
  message: string;
  requestId?: string;
}

function ReviewForm({
  mode,
  patientId,
  allergyId,
  allergy: a,
  onDone,
}: {
  mode: 'confirm' | 'reject';
  patientId: string;
  allergyId: string;
  allergy: AllergyListItem;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { confirmAllergy, rejectAllergy } = usePatientActions();
  const [group, setGroup] = useState<string>(
    isGroup(a.allergyGroup) ? a.allergyGroup : '',
  );
  const [severity, setSeverity] = useState<string>(a.severity ?? '');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const confirming = mode === 'confirm';
  const pending = confirmAllergy.isPending || rejectAllergy.isPending;
  const reasonText = reason.trim();
  const reasonError =
    !confirming && touched && reasonText.length < 2
      ? 'Give the reason (at least 2 characters). The patient sees it.'
      : fieldErrors.reason;

  async function submit() {
    if (!confirming && reasonText.length < 2) {
      setTouched(true);
      return;
    }
    setFailure(null);
    setFieldErrors({});
    try {
      if (confirming) {
        await confirmAllergy.mutateAsync({
          id: patientId,
          allergyId,
          allergyGroup: isGroup(group) ? group : undefined,
          severity: (severity || undefined) as AllergySeverity | undefined,
        });
        toast.show({
          tone: 'success',
          title: `${a.substance} confirmed`,
          body: 'The patient is told their allergy record was updated.',
        });
      } else {
        await rejectAllergy.mutateAsync({
          id: patientId,
          allergyId,
          reason: reasonText,
        });
        toast.show({
          tone: 'success',
          title: `${a.substance} rejected`,
          body: 'It is off the record; the patient sees your reason in the app.',
        });
      }
      onDone();
    } catch (err) {
      if (!isApiError(err)) {
        setFailure({
          message: confirming
            ? 'Could not confirm the allergy.'
            : 'Could not reject the allergy.',
        });
        return;
      }
      const gone =
        err.code === 'ALLERGY_NOT_REPORTED' || err.code === 'ALLERGY_NOT_FOUND';
      setFieldErrors(
        Object.fromEntries(err.fieldErrors.map((f) => [f.field, f.message])),
      );
      setFailure({
        message:
          err.code === 'ALLERGY_NOT_REPORTED'
            ? 'This allergy is no longer waiting for confirmation (it was confirmed already, or another doctor decided first). The record has been refreshed.'
            : err.code === 'ALLERGY_NOT_FOUND'
              ? 'This allergy is no longer on the record. The record has been refreshed.'
              : err.message,
        requestId: err.requestId,
      });
      if (gone) {
        void qc.invalidateQueries({ queryKey: ['patient', patientId] });
        void qc.invalidateQueries({ queryKey: ['patient-record'] });
        void qc.invalidateQueries({ queryKey: ['encounter'] });
        void qc.invalidateQueries({ queryKey: ['queue'] });
      }
    }
  }

  return (
    <div
      className={styles.form}
      role="group"
      aria-label={`${confirming ? 'Confirm' : 'Reject'} allergy ${a.substance}`}
      onKeyDown={(e) => {
        // The patient drawer is a form: Enter here acts on this allergy only.
        if (e.key !== 'Enter' || e.target instanceof HTMLTextAreaElement)
          return;
        if (e.target instanceof HTMLButtonElement) return;
        e.preventDefault();
        if (!pending) void submit();
      }}
    >
      {confirming ? (
        <>
          <p className={styles.note}>
            Check it with the patient. Set the group so related medicines are
            blocked at prescribing too.
          </p>
          <div className={styles.fields}>
            <Field label="Allergy group" error={fieldErrors.allergyGroup}>
              <Select
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                options={[
                  { value: '', label: 'No group' },
                  ...GROUPS.map((g) => ({
                    value: g,
                    label: ALLERGY_GROUP_LABELS[g],
                  })),
                ]}
              />
            </Field>
            <Field label="Severity" error={fieldErrors.severity}>
              <Select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                options={[
                  { value: '', label: 'Not given' },
                  ...SEVERITIES.map((s) => ({
                    value: s,
                    label: ALLERGY_SEVERITY_LABELS[s],
                  })),
                ]}
              />
            </Field>
          </div>
        </>
      ) : (
        <Field label="Reason (shown to the patient)" error={reasonError}>
          <textarea
            className={styles.textarea}
            rows={2}
            maxLength={200}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
      )}
      {failure && (
        <p className={styles.error} role="alert">
          {failure.message}
          {failure.requestId && (
            <span>{t('common.requestId', { id: failure.requestId })}</span>
          )}
        </p>
      )}
      <span className={styles.actions}>
        <Button
          size="sm"
          variant={confirming ? 'primary' : 'danger'}
          loading={pending}
          onClick={() => void submit()}
        >
          {confirming ? 'Confirm allergy' : 'Reject allergy'}
        </Button>
        <Button size="sm" onClick={onDone}>
          Cancel
        </Button>
      </span>
    </div>
  );
}
